import { useReducer, useCallback, useRef, useEffect, useState } from 'react';
import type {
  Message,
  MessageType,
  ToolCall,
  SourceCitation,
  ChatWidgetProps,
  SessionConfig,
  RetryPolicy,
  RichContent,
} from '../types';
import { MESSAGE_TYPES } from '../constants/messageTypes';
import { STORAGE_KEY } from '../constants/storage';
import { chatReducer, initialChatState } from './chatReducer';
import { fetchWithTimeout } from '../utils/fetchWithTimeout';
import { getSessionId } from '../utils/session';
import { resolveEndpoint, getNextMessageId } from '../utils/chatHelpers';
import { createStreamingMessageFn } from './streamingMessage';

interface UseChatOptions {
  enableStreaming: boolean;
  enableToolVisualization: boolean;
  enableRichMessages: boolean;
  enableSourceCitations: boolean;
  enableThinking: boolean;
  streamEndpoint: string | null | undefined;
  messageEndpoint: string | null | undefined;
  messageCreateEndpoint: string | null | undefined;
  apiBaseUrl: string | null | undefined;
  apiKey: string | null | undefined;
  provider?: string;
  subject: string;
  department: string;
  channel: string;
  customFields: Record<string, unknown>;
  metaExtras?: Record<string, unknown>;
  threadId: string | null;
  threadBase: string | null;
  companyName: string;
  saveHistory: boolean;
  loadHistory?: boolean;
  buildMessagePayload?: ChatWidgetProps['buildMessagePayload'];
  parseResponse?: ChatWidgetProps['parseResponse'];
  onSend?: ChatWidgetProps['onSend'];
  onReceive?: ChatWidgetProps['onReceive'];
  onError?: ChatWidgetProps['onError'];
  onMessageCreate?: ChatWidgetProps['onMessageCreate'];
  trackAnalytics: (action: string, data: Record<string, unknown>) => void;
  withAuthHeaders: (headers?: Record<string, string>) => Record<string, string>;
  fetchThread: () => Promise<void>;
  createThread: (args?: {
    subject?: string;
    message?: string;
    meta?: Record<string, unknown>;
    ch?: string;
  }) => Promise<{ threadId: string; raw: unknown }>;
  threadCreateEndpoint: string | null | undefined;
  minimized: boolean;
  setThreadId: (id: string | null) => void;
  sessionConfig?: SessionConfig;
  retryPolicy?: RetryPolicy;
}

export function useChat(options: UseChatOptions) {
  const {
    enableStreaming,
    enableToolVisualization,
    enableRichMessages,
    enableSourceCitations,
    enableThinking,
    streamEndpoint,
    messageEndpoint,
    messageCreateEndpoint,
    apiBaseUrl,
    subject,
    provider,
    department,
    channel,
    customFields,
    metaExtras,
    threadId,
    threadBase,
    companyName,
    saveHistory,
    loadHistory = true,
    buildMessagePayload,
    parseResponse,
    onSend,
    onReceive,
    onError,
    onMessageCreate,
    trackAnalytics,
    withAuthHeaders,
    fetchThread,
    createThread,
    threadCreateEndpoint,
    setThreadId,
    sessionConfig,
    retryPolicy,
  } = options;

  const [state, dispatch] = useReducer(chatReducer, initialChatState);
  const [historyLoaded, setHistoryLoaded] = useState(!(loadHistory && saveHistory));
  const abortControllerRef = useRef<AbortController | null>(null);
  const mountedRef = useRef(true);
  const stateRef = useRef(state);

  const abortActiveStream = useCallback(() => {
    abortControllerRef.current?.abort();
  }, []);

  useEffect(() => {
    stateRef.current = state;
  }, [state]);

  // Abort in-flight stream and mark unmounted
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      abortActiveStream();
    };
  }, [abortActiveStream]);

  // Load history from localStorage on mount
  useEffect(() => {
    if (!loadHistory || !saveHistory) {
      setHistoryLoaded(true);
      return;
    }
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (raw) {
        const parsed = JSON.parse(raw);
        if (Array.isArray(parsed)) dispatch({ type: 'SET_MESSAGES', messages: parsed });
      }
    } catch {
      // localStorage may be unavailable (private browsing, storage full, SSR)
    } finally {
      setHistoryLoaded(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const setMessages = useCallback((updater: Message[] | ((prev: Message[]) => Message[])) => {
    const next = typeof updater === 'function' ? updater(stateRef.current.messages) : updater;
    dispatch({ type: 'SET_MESSAGES', messages: next });
  }, []);

  const handleJsonResponse = useCallback(
    async (
      data: Record<string, unknown>,
      localMsg: Message,
      atts: { name: string; type: string; size: number; dataUrl?: string }[]
    ) => {
      const parsed = typeof parseResponse === 'function' ? parseResponse(data) : null;

      let messageType: MessageType = MESSAGE_TYPES.TEXT;
      let richContent: RichContent | undefined = undefined;

      if (enableRichMessages && data.type) {
        messageType = data.type as MessageType;
        richContent = ((data as Record<string, unknown>).content ||
          (data as Record<string, unknown>).richContent) as RichContent;
      }

      if (enableRichMessages && messageType === MESSAGE_TYPES.PRODUCT_CARDS && !richContent) {
        const products =
          (data as { products?: unknown; recommendations?: unknown }).products ||
          (data as { recommendations?: unknown }).recommendations;
        if (Array.isArray(products)) {
          richContent = { products: products as RichContent['products'] };
        }
      }

      const reply: Message = {
        id: `${getNextMessageId()}-ai`,
        sender: 'ai',
        text:
          (parsed as { text?: string } | null)?.text ??
          (data as { reply?: string }).reply ??
          (data as { message?: string }).message ??
          (data as { text?: string }).text ??
          'Thanks! We received your message.',
        timestamp: new Date().toLocaleTimeString(),
        status: 'delivered',
        type: messageType,
        richContent,
        suggestions:
          (parsed as { suggestions?: string[] } | null)?.suggestions ??
          (data as { suggestions?: string[] }).suggestions ??
          [],
        sources: enableSourceCitations
          ? (((data as { sources?: unknown; citations?: unknown }).sources ||
              (data as { citations?: unknown }).citations) as SourceCitation[] | undefined)
          : undefined,
        toolCalls: enableToolVisualization
          ? ((data as { toolCalls?: unknown }).toolCalls as ToolCall[] | undefined)
          : undefined,
      };

      if (messageType === MESSAGE_TYPES.PRODUCT_CARDS && richContent?.products?.length) {
        trackAnalytics('product_recommendations', {
          count: richContent.products.length,
          ids: richContent.products.map(p => p?.id).filter(Boolean),
        });
      }

      const newThreadId =
        (parsed as { threadId?: string } | null)?.threadId ??
        (data as { threadId?: string }).threadId ??
        (data as { thread?: { id?: string } }).thread?.id ??
        null;
      if (newThreadId && newThreadId !== threadId) setThreadId(newThreadId);
      dispatch({ type: 'ADD_MESSAGE', message: reply });
      onMessageCreate?.(localMsg, reply);
      trackAnalytics('message_sent', { hasAttachments: atts?.length > 0 });
      try {
        await fetchThread();
      } catch {
        // Thread refresh after message send is best-effort; failure is non-critical
      }
      return { request: localMsg, response: reply, raw: data };
    },
    [
      parseResponse,
      enableRichMessages,
      enableSourceCitations,
      enableToolVisualization,
      threadId,
      setThreadId,
      onMessageCreate,
      trackAnalytics,
      fetchThread,
    ]
  );

  const readFileAsDataUrl = useCallback((file: File) => {
    return new Promise<string>((resolve, reject) => {
      const reader = new FileReader();
      reader.onerror = () => reject(reader.error || new Error('Failed to read file'));
      reader.onload = () => resolve(typeof reader.result === 'string' ? reader.result : '');
      reader.readAsDataURL(file);
    });
  }, []);

  const buildAttachmentPayload = useCallback(
    async (
      attachments: { name: string; type: string; size: number; file?: File }[]
    ): Promise<{ name: string; type: string; size: number; dataUrl?: string }[]> => {
      if (!attachments || attachments.length === 0) return [];
      return Promise.all(
        attachments.map(async att => {
          const base = { name: att.name, type: att.type, size: att.size };
          if (att.file && typeof FileReader !== 'undefined') {
            try {
              const dataUrl = await readFileAsDataUrl(att.file);
              return { ...base, dataUrl };
            } catch {
              return base;
            }
          }
          return base;
        })
      );
    },
    [readFileAsDataUrl]
  );

  // Shared payload builder for streaming and non-streaming
  const buildPayload = useCallback(
    ({
      text,
      attachments: atts = [],
      subject: subj,
      tid,
      meta,
    }: {
      text: string;
      attachments?: { name: string; type: string; size: number; dataUrl?: string }[];
      subject?: string;
      tid?: string;
      meta?: Record<string, unknown>;
    }) => ({
      subject: subj || subject || (text ? text.slice(0, 80) : 'New message'),
      message: text,
      attachments: (atts || []).map(a => ({
        name: a.name,
        type: a.type,
        size: a.size,
        ...(a.dataUrl ? { dataUrl: a.dataUrl } : {}),
      })),
      meta: { department, customFields, ...(metaExtras || {}), ...(meta || {}) },
      sessionId: getSessionId(sessionConfig),
      source: 'response-chat-widget',
      ...(provider ? { provider } : {}),
      channel,
      threadId: tid || threadId,
    }),
    [subject, department, customFields, metaExtras, provider, channel, threadId, sessionConfig]
  );

  const fetchWithRetry = useCallback(
    async (url: string, init: RequestInit & { timeoutMs?: number }) => {
      const retries = retryPolicy?.retries ?? 0;
      const backoffMs = retryPolicy?.backoffMs ?? 500;
      const retryStatuses = retryPolicy?.retryOnStatuses ?? [429, 500, 502, 503, 504];

      let attempt = 0;
      while (true) {
        try {
          const res = await fetchWithTimeout(url, init);
          if (!res.ok && attempt < retries && retryStatuses.includes(res.status)) {
            attempt += 1;
            await new Promise(resolve =>
              setTimeout(resolve, Math.min(backoffMs * attempt, 10_000))
            );
            continue;
          }
          return res;
        } catch (err) {
          if (attempt < retries) {
            attempt += 1;
            await new Promise(resolve =>
              setTimeout(resolve, Math.min(backoffMs * attempt, 10_000))
            );
            continue;
          }
          throw err;
        }
      }
    },
    [retryPolicy]
  );

  // Streaming message handler (delegated to streamingMessage.ts)
  const createStreamingMessage = useCallback(
    (args: {
      text: string;
      attachments?: { name: string; type: string; size: number; dataUrl?: string }[];
      subject?: string;
      tid?: string;
      sender?: 'user' | 'ai';
      meta?: Record<string, unknown>;
    }) =>
      createStreamingMessageFn({
        ...args,
        streamEndpoint,
        messageCreateEndpoint,
        messageEndpoint,
        apiBaseUrl,
        threadId,
        threadBase,
        dispatch,
        mountedRef,
        abortControllerRef,
        withAuthHeaders,
        buildPayload,
        buildMessagePayload,
        handleJsonResponse,
        onMessageCreate,
        trackAnalytics,
        enableToolVisualization,
        enableSourceCitations,
        enableThinking,
        enableRichMessages,
      }),
    [
      streamEndpoint,
      messageCreateEndpoint,
      messageEndpoint,
      apiBaseUrl,
      threadId,
      threadBase,
      withAuthHeaders,
      buildMessagePayload,
      buildPayload,
      handleJsonResponse,
      onMessageCreate,
      trackAnalytics,
      enableToolVisualization,
      enableSourceCitations,
      enableThinking,
      enableRichMessages,
    ]
  );

  const createMessage = useCallback(
    async ({
      text,
      attachments: atts = [],
      subject: subj,
      tid,
      sender = 'user',
      meta,
    }: {
      text: string;
      attachments?: { name: string; type: string; size: number; dataUrl?: string }[];
      subject?: string;
      tid?: string;
      sender?: 'user' | 'ai';
      meta?: Record<string, unknown>;
    }) => {
      if (enableStreaming && (streamEndpoint || messageEndpoint)) {
        return createStreamingMessage({
          text,
          attachments: atts,
          subject: subj,
          tid,
          sender,
          meta,
        });
      }

      const endpoint = resolveEndpoint({
        messageCreate: messageCreateEndpoint,
        message: messageEndpoint,
        threadBase,
        tid: tid || threadId,
        apiBaseUrl,
      });
      if (!endpoint) throw new Error('No message endpoint configured');

      const now = new Date();
      const localMsg: Message = {
        id: getNextMessageId(),
        sender,
        text,
        timestamp: now.toLocaleTimeString(),
        status: 'sent',
      };
      if (!(meta && typeof meta === 'object' && meta.silent === true)) {
        dispatch({ type: 'ADD_MESSAGE', message: localMsg });
      }

      const payload = buildPayload({ text, attachments: atts, subject: subj, tid, meta });
      const bodyPayload =
        typeof buildMessagePayload === 'function'
          ? buildMessagePayload(payload, localMsg)
          : payload;
      const res = await fetchWithRetry(endpoint, {
        method: 'POST',
        headers: withAuthHeaders(),
        body: JSON.stringify(bodyPayload),
        timeoutMs: 30_000,
      });
      if (!res.ok) throw new Error(`Message create failed: ${res.status}`);
      const data = await res.json();
      return await handleJsonResponse(data as Record<string, unknown>, localMsg, atts);
    },
    [
      enableStreaming,
      streamEndpoint,
      createStreamingMessage,
      messageCreateEndpoint,
      messageEndpoint,
      apiBaseUrl,
      threadId,
      threadBase,
      withAuthHeaders,
      buildMessagePayload,
      buildPayload,
      handleJsonResponse,
      fetchWithRetry,
    ]
  );

  const handleSend = useCallback(
    async (
      attachments: {
        id: string;
        name: string;
        type: string;
        size: number;
        file?: File;
        previewUrl?: string | null;
      }[]
    ) => {
      const text = state.input.trim();
      if (!text && attachments.length === 0) return;
      dispatch({ type: 'SET_INPUT', input: '' });
      dispatch({ type: 'SEND_START' });

      try {
        let ensuredThreadId = threadId;
        const subj = subject || (text ? text.slice(0, 80) : `New message from ${companyName}`);

        if (!ensuredThreadId && threadCreateEndpoint) {
          const created = await createThread({ subject: subj, ch: channel });
          ensuredThreadId = created?.threadId || threadId;
        }

        const atts = attachments.length > 0 ? await buildAttachmentPayload(attachments) : [];
        const result = await createMessage({
          text,
          attachments: atts,
          subject: subj,
          tid: ensuredThreadId || undefined,
        });
        onSend?.(result?.request);
        trackAnalytics('message_received', {});
        onReceive?.(result?.response);
        dispatch({ type: 'SEND_SUCCESS' });
      } catch (err) {
        const errorObj = err instanceof Error ? err : new Error(String(err));
        const userMessage =
          errorObj.message && errorObj.message !== 'Failed to fetch'
            ? `Failed to send: ${errorObj.message}`
            : 'Failed to send. Please try again.';
        dispatch({ type: 'SEND_ERROR', error: userMessage });
        onError?.(errorObj);
      }
    },
    [
      state.input,
      threadId,
      subject,
      companyName,
      threadCreateEndpoint,
      createThread,
      channel,
      createMessage,
      onSend,
      onReceive,
      onError,
      trackAnalytics,
      buildAttachmentPayload,
    ]
  );

  return {
    state,
    dispatch,
    setMessages,
    createMessage,
    handleSend,
    historyLoaded,
  };
}
